import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { X, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { formatINR, formatCurrencyDetailed } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function InvestorRow({ index, data, onChange, onRemove, canRemove }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-[#111] border border-white/5 rounded-sm">
      <div className="flex-1 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input
            placeholder="Investor Name"
            value={data.name}
            onChange={(e) => onChange(index, 'name', e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white text-sm"
            data-testid={`investor-name-${index}`}
          />
          <Input
            placeholder="+91 9876543210"
            value={data.phone}
            onChange={(e) => onChange(index, 'phone', e.target.value)}
            className="bg-[#0A0A0A] border-white/10 text-white text-sm font-mono"
            data-testid={`investor-phone-${index}`}
          />
          <Input
            type="number"
            placeholder="Amount (INR)"
            value={data.amount || ''}
            onChange={(e) => onChange(index, 'amount', parseFloat(e.target.value) || 0)}
            className="bg-[#0A0A0A] border-white/10 text-white text-sm font-mono"
            data-testid={`investor-amount-${index}`}
          />
        </div>
      </div>
      {canRemove && (
        <button
          onClick={() => onRemove(index)}
          className="p-2 text-white/20 hover:text-red-400 transition-colors mt-1"
          data-testid={`remove-investor-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function AssignmentModal({ onClose, onCreated }) {
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [investors, setInvestors] = useState([{ name: '', phone: '', amount: 0 }]);
  const [expiryHours, setExpiryHours] = useState(48);
  const [submitting, setSubmitting] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(true);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await api.get('/projects');
      const allUnits = [];
      for (const project of (res.data || [])) {
        for (const unit of (project.units || [])) {
          const status = unit.lifecycle?.currentState || unit.status || '';
          if (['OPEN', 'PARTIALLY_FUNDED'].includes(status)) {
            const target = unit.financial?.capitalTarget || unit.targetAmount || 0;
            const raised = unit.financial?.capitalRaised || unit.capitalRaised || 0;
            const reserved = unit.reservedAmount || 0;
            allUnits.push({
              unitId: unit.unitId,
              unitNumber: unit.unitNumber || unit.unitName,
              projectName: project.name,
              projectId: project.projectId,
              targetAmount: target,
              capitalRaised: raised,
              reservedAmount: reserved,
              available: Math.max(0, target - raised - reserved),
            });
          }
        }
      }
      setUnits(allUnits);
    } catch {
      toast.error('Failed to load units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleUnitChange = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    setSelectedUnit(unit || null);
  };

  const handleInvestorChange = (index, field, value) => {
    setInvestors(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addInvestor = () => {
    setInvestors(prev => [...prev, { name: '', phone: '', amount: 0 }]);
  };

  const removeInvestor = (index) => {
    setInvestors(prev => prev.filter((_, i) => i !== index));
  };

  const totalAmount = investors.reduce((s, i) => s + (i.amount || 0), 0);
  const isOverCapacity = selectedUnit && totalAmount > selectedUnit.available;

  const canSubmit = selectedUnit
    && investors.every(i => i.phone && i.amount > 0)
    && totalAmount > 0
    && !isOverCapacity
    && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post('/broker/pipeline/assignments', {
        unitId: selectedUnit.unitId,
        assignments: investors.map(i => ({
          phone: i.phone.startsWith('+') ? i.phone : `+91${i.phone.replace(/\D/g, '')}`,
          name: i.name,
          amount: i.amount,
        })),
        expiryHours,
      });
      toast.success(`${res.data.data?.created || investors.length} assignments created`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assignments');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4" data-testid="assignment-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <h2 className="text-lg font-light text-white">Create Assignment</h2>
            <p className="text-xs text-white/40 mt-0.5">Assign investors to a unit</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Unit Selector */}
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider mb-1.5 block">Select Unit</label>
            {loadingUnits ? (
              <div className="flex items-center gap-2 text-white/30 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading units...
              </div>
            ) : (
              <select
                onChange={(e) => handleUnitChange(e.target.value)}
                className="w-full bg-[#111] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#4ADE80]/50"
                data-testid="unit-selector"
              >
                <option value="">Choose a unit...</option>
                {units.map(u => (
                  <option key={u.unitId} value={u.unitId}>
                    {u.projectName} - {u.unitNumber} (Available: {formatINR(u.available)})
                  </option>
                ))}
              </select>
            )}

            {/* Unit capacity bar */}
            {selectedUnit && (
              <div className="mt-3 p-3 bg-[#111] border border-white/5 rounded-sm">
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-white/40">Capacity</span>
                  <span className="font-mono text-white/60">
                    {formatINR(selectedUnit.capitalRaised + selectedUnit.reservedAmount)} / {formatINR(selectedUnit.targetAmount)}
                  </span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#4ADE80]/60 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((selectedUnit.capitalRaised + selectedUnit.reservedAmount) / selectedUnit.targetAmount) * 100)}%` }}
                  />
                </div>
                <p className="text-xs font-mono text-[#4ADE80] mt-1.5">
                  Available: {formatINR(selectedUnit.available)}
                </p>
              </div>
            )}
          </div>

          {/* Investors */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-white/40 uppercase tracking-wider">Investors</label>
              <button
                onClick={addInvestor}
                className="flex items-center gap-1 text-xs text-[#4ADE80] hover:text-[#4ADE80]/80 transition-colors"
                data-testid="add-investor-btn"
              >
                <Plus className="w-3 h-3" /> Add Another
              </button>
            </div>
            <div className="space-y-2">
              {investors.map((inv, i) => (
                <InvestorRow
                  key={i}
                  index={i}
                  data={inv}
                  onChange={handleInvestorChange}
                  onRemove={removeInvestor}
                  canRemove={investors.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="p-3 bg-[#111] border border-white/5 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/40">Total Assigned</span>
              <span className={`text-sm font-mono ${isOverCapacity ? 'text-red-400' : 'text-white'}`}>
                {formatCurrencyDetailed(totalAmount)}
              </span>
            </div>
            {isOverCapacity && (
              <div className="flex items-center gap-1.5 mt-2 text-red-400">
                <AlertCircle className="w-3.5 h-3.5" />
                <span className="text-xs">Exceeds available capacity by {formatINR(totalAmount - selectedUnit.available)}</span>
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-white/40">Link Expiry</span>
              <select
                value={expiryHours}
                onChange={(e) => setExpiryHours(parseInt(e.target.value))}
                className="bg-[#0A0A0A] border border-white/10 text-xs text-white/70 px-2 py-1 rounded-sm"
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
                <option value={168}>7 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/5">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/10 text-white/50 bg-transparent hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 disabled:opacity-30"
            data-testid="submit-assignment-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating...</>
            ) : (
              <><Plus className="w-4 h-4 mr-1" /> Create {investors.length} Assignment{investors.length > 1 ? 's' : ''}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
